import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { stringifyGraph } from '@/clients/http'
import { PosReportModel, type TerminalShift } from '@/http/models/PosReport'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'

import { Badge } from '@/components/ui/badge'

import { PageHeader } from '@/components/atoms'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { formatDateTime, signed } from '@/components/features/pos/format'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { ShiftReportDialog } from '@/components/features/pos/ShiftReportDialog'
import { usePagedReport } from '@/components/features/pos/usePagedReport'
import { usePosLookups } from '@/components/features/pos/usePosLookups'

const GRAPH = stringifyGraph({ '*': true, staff: { name: true } })

/** Every shift on every till (checklist C24). Opened and closed on the till, synced; read-only here. */
const ShiftHistory = () => {
  const { can } = useAuth()
  const { formatCurrency } = useBusiness()
  const lookups = usePosLookups()
  // Terminals → View opens this page for one terminal.
  const [params] = useSearchParams()
  const [terminal, setTerminal] = useState(() => params.get('terminal_id') ?? '')
  const [open, setOpen] = useState<TerminalShift | null>(null)
  const list = usePagedReport<TerminalShift>(PosReportModel.getShifts, { terminal_id: terminal || undefined, graph_enc: GRAPH })
  const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatCurrency(n))

  return (
    <div className="space-y-4">
      <PageHeader title="Shifts" description="Shifts on every terminal, newest first. Select a shift for its sales breakdown." />
      <PosListCard<TerminalShift>
        {...list}
        onPageChange={list.setPage}
        empty="No shifts yet."
        onRowClick={can('pos_reports:shifts') ? setOpen : undefined}
        filters={<FilterSelect label="Terminal" value={terminal} onChange={setTerminal} allLabel="All terminals" options={lookups.terminalOptions} />}
        columns={[
          { label: 'Shift', render: (s) => <span className="font-mono text-xs">{s.shift_no ?? '—'}</span>, className: 'hidden md:table-cell' },
          { label: 'Opened', render: (s) => formatDateTime(s.opened_at) },
          { label: 'Closed', render: (s) => (s.closed_at ? formatDateTime(s.closed_at) : <Badge tone="success">Open</Badge>) },
          { label: 'Branch', render: (s) => lookups.branchOfTerminal(s.terminal_id), className: 'hidden md:table-cell' },
          { label: 'Terminal', render: (s) => lookups.terminalName(s.terminal_id) },
          { label: 'Staff', render: (s) => s.staff?.name ?? '—', className: 'hidden md:table-cell' },
          { label: 'Opening', render: (s) => <span className="tabular-nums">{money(s.opening_balance)}</span>, className: 'hidden text-right lg:table-cell' },
          { label: 'Cash in', render: (s) => <span className="tabular-nums">{money(s.total_cash_in)}</span>, className: 'hidden text-right lg:table-cell' },
          { label: 'Cash out', render: (s) => <span className="tabular-nums">{money(s.total_cash_out)}</span>, className: 'hidden text-right lg:table-cell' },
          { label: 'Expected', render: (s) => <span className="tabular-nums">{money(s.expected_balance)}</span>, className: 'text-right' },
          { label: 'Counted', render: (s) => <span className="tabular-nums">{money(s.closing_balance)}</span>, className: 'text-right' },
          {
            label: 'Difference',
            render: (s) => <span className={s.discrepancy ? 'tabular-nums text-error' : 'tabular-nums'}>{signed(s.discrepancy, formatCurrency)}</span>,
            className: 'text-right',
          },
        ]}
      />
      <ShiftReportDialog shiftId={open?._id ?? null} onClose={() => setOpen(null)} terminalName={lookups.terminalName(open?.terminal_id)} shiftNo={open?.shift_no ?? undefined} />
    </div>
  )
}

export default ShiftHistory
